import { useEffect } from 'react';
import { X, MapPin, Clock, Shield, Briefcase, DollarSign, ArrowRight, ListChecks, GraduationCap } from 'lucide-react';

function splitLines(text) {
  if (!text) return [];
  return String(text)
    .split(/[\n;,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function JobDetailsModal({ open, onClose, job, onApply }) {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open || !job) return null;

  const responsibilities = splitLines(job.responsibilities);
  const requirements = splitLines(job.requirements);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="job-details-modal"
    >
      <div className="bg-[#121212] border border-white/10 w-full max-w-3xl my-8 relative">
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-white/10 relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#F2B705] to-transparent" />
          <button onClick={onClose} data-testid="job-details-close-btn" className="absolute top-4 right-4 text-[#888] hover:text-white p-2">
            <X size={20} />
          </button>
          <p className="text-xs uppercase tracking-[0.3em] text-[#F2B705] font-bold mb-3">Open Position</p>
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight pr-8">{job.title}</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
            {job.location && (
              <span className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                <MapPin size={12} className="text-[#F2B705]" /> {job.location}
              </span>
            )}
            {(job.employment_type || job.type) && (
              <span className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                <Clock size={12} className="text-[#F2B705]" /> {job.employment_type || job.type}
              </span>
            )}
            {job.work_arrangement && (
              <span className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                <Briefcase size={12} className="text-[#F2B705]" /> {job.work_arrangement}
              </span>
            )}
            {job.clearance && (
              <span className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                <Shield size={12} className="text-[#F2B705]" /> {job.clearance}
              </span>
            )}
            {job.salary_range && (
              <span className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                <DollarSign size={12} className="text-[#F2B705]" /> {job.salary_range}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8 space-y-8">
          {(job.description || job.desc) && (
            <div>
              <h3 className="text-xs uppercase tracking-widest text-[#F2B705] font-bold mb-3">Job Summary</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">{job.description || job.desc}</p>
            </div>
          )}

          {responsibilities.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-widest text-[#F2B705] font-bold mb-3 flex items-center gap-2">
                <ListChecks size={12} /> Responsibilities
              </h3>
              <ul className="space-y-2.5">
                {responsibilities.map((r) => (
                  <li key={`resp-${r}`} className="flex items-start gap-3">
                    <span className="text-[#F2B705] mt-1 shrink-0">•</span>
                    <span className="text-sm text-[#A3A3A3] leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {requirements.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-widest text-[#F2B705] font-bold mb-3 flex items-center gap-2">
                <GraduationCap size={12} /> Qualifications
              </h3>
              <ul className="space-y-2.5">
                {requirements.map((r) => (
                  <li key={`req-${r}`} className="flex items-start gap-3">
                    <span className="text-[#F2B705] mt-1 shrink-0">•</span>
                    <span className="text-sm text-[#A3A3A3] leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer / CTA */}
        <div className="p-6 md:p-8 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between bg-[#0E0E0E]">
          <p className="text-xs text-[#666]">Submitting takes about a minute.</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="border border-white/10 text-[#A3A3A3] font-bold text-xs uppercase tracking-wide px-6 py-3 hover:text-white transition-colors">
              Close
            </button>
            <button
              onClick={() => onApply?.(job)}
              data-testid="job-details-apply-btn"
              className="bg-[#F2B705] text-black font-bold text-xs uppercase tracking-wide px-6 py-3 hover:bg-[#D9A404] transition-colors inline-flex items-center gap-2"
            >
              Apply for This Position <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
